import { useEffect, useState } from "react";
import type { RadarStateSource } from "@radar-sim/engine";
import type { TargetState, WorldSnapshot } from "@radar-sim/schema";
import { Card, Heading, Pill, Stack } from "../ui/primitives.js";
import type { MarpaTrack, MarpaTracker, MarpaPhase } from "../ppi/marpa.js";
import {
  dayShapeFor,
  describeDayShape,
  fogSignalFor,
  lightsFor,
} from "../identification/vesselIdentity.js";
import { DayShapeIcon } from "../identification/DayShapeIcon.js";
import { LightArrangementDiagram } from "../identification/LightArrangementDiagram.js";

/**
 * AIS / target-info readout for MARPA-acquired vessels. Shows the
 * vessel's static AIS plate (name, MMSI, class, callsign, destination)
 * alongside the live-derived fields the student is being graded on:
 * range, bearing, course, speed, CPA and TCPA.
 *
 * Read-only — acquisition / drop happens by tapping a target on the PPI.
 */
export function TargetReadout(props: {
  source: RadarStateSource;
  marpa: MarpaTracker;
}): JSX.Element {
  const [tracks, setTracks] = useState<ReadonlyArray<MarpaTrack>>(() =>
    props.marpa.list(),
  );
  const [snapshot, setSnapshot] = useState<WorldSnapshot | null>(null);

  useEffect(() => {
    const stop = props.marpa.subscribe((t) => setTracks(t));
    return stop;
  }, [props.marpa]);

  useEffect(() => {
    const stop = props.source.subscribe((s) => setSnapshot(s));
    return stop;
  }, [props.source]);

  return (
    <Card padding={16} style={{ width: "100%" }}>
      <Stack gap={12}>
        <Stack direction="row" gap={8} align="center" justify="between">
          <Heading level={2}>Targets</Heading>
          <Pill tone={tracks.length > 0 ? "accent" : "neutral"}>
            {tracks.length === 0
              ? "None acquired"
              : `${tracks.length} tracking`}
          </Pill>
        </Stack>
        {tracks.length === 0 ? (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "var(--text-muted)",
              lineHeight: 1.5,
            }}
          >
            Tap a vessel on the radar to acquire it under MARPA. The solution
            takes about a minute to settle; CPA/TCPA appear here once tracked.
          </p>
        ) : (
          <Stack gap={10}>
            {tracks.map((t) => {
              const target = snapshot?.targets.find(
                (tg) => tg.id === t.vesselId,
              );
              return (
                <TrackCard
                  key={t.vesselId}
                  track={t}
                  target={target}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

const PHASE_LABEL: Record<MarpaPhase, string> = {
  acquiring: "ACQUIRING",
  building: "BUILDING",
  tracked: "TRACKED",
  lost: "LOST",
};

function TrackCard(props: {
  track: MarpaTrack;
  target: TargetState | undefined;
}): JSX.Element {
  const t = props.track;
  const ais = props.target?.ais;
  const cpaPill: "danger" | "accent" | "neutral" =
    t.phase === "lost"
      ? "neutral"
      : t.cpaNm < 0.3
        ? "danger"
        : t.cpaNm < 1
          ? "accent"
          : "neutral";

  return (
    <div
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-sm)",
        padding: 12,
      }}
    >
      <Stack gap={8}>
        <Stack direction="row" gap={6} align="center" wrap>
          <strong style={{ fontSize: 14, color: "var(--text)" }}>
            {ais?.name ?? props.target?.id ?? t.vesselId}
          </strong>
          <Pill tone={t.phase === "lost" ? "danger" : "accent"}>
            {PHASE_LABEL[t.phase]}
          </Pill>
          {ais?.class !== undefined && <Pill>AIS-{ais.class}</Pill>}
        </Stack>
        {ais !== undefined && (ais.mmsi !== undefined || ais.callsign !== undefined || ais.destination !== undefined) && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text-muted)",
              lineHeight: 1.5,
            }}
          >
            {ais.mmsi !== undefined && <div>MMSI {ais.mmsi}</div>}
            {ais.callsign !== undefined && <div>Callsign {ais.callsign}</div>}
            {ais.destination !== undefined && (
              <div>Dest. {ais.destination}</div>
            )}
          </div>
        )}
        <Stack direction="row" gap={12} wrap>
          <Stat label="RNG" value={`${t.rangeNm.toFixed(2)} nm`} />
          <Stat label="BRG" value={`${t.bearingDeg.toFixed(0).padStart(3, "0")}°`} />
          {props.target !== undefined && (
            <>
              <Stat
                label="COG"
                value={`${props.target.cog.toFixed(0).padStart(3, "0")}°`}
              />
              <Stat label="SOG" value={`${props.target.sog.toFixed(1)} kn`} />
            </>
          )}
        </Stack>
        <Stack direction="row" gap={12} wrap align="center">
          <Stat label="CPA" value={`${t.cpaNm.toFixed(2)} nm`} tone={cpaPill} />
          <Stat
            label="TCPA"
            value={
              t.phase === "lost"
                ? "—"
                : !Number.isFinite(t.tcpaMin)
                  ? "—"
                  : t.tcpaMin < 0
                    ? `${Math.abs(t.tcpaMin).toFixed(1)} min (open)`
                    : `${t.tcpaMin.toFixed(1)} min`
            }
          />
        </Stack>
        {props.target !== undefined && (
          <Identification target={props.target} />
        )}
      </Stack>
    </div>
  );
}

/**
 * Identification panel for an acquired target. Shows the COLREG day
 * shape (or "no shape required" message), the navigation-light
 * arrangement for night recognition, and the Rule 35 fog signal —
 * three forms of identification that pair with the radar paint.
 */
function Identification(props: { target: TargetState }): JSX.Element {
  const t = props.target;
  const shape = dayShapeFor(t.vesselType, t.state);
  const lights = lightsFor(t.vesselType, t.state);
  const fog = fogSignalFor(t.vesselType, t.state);
  return (
    <div
      style={{
        marginTop: 4,
        paddingTop: 8,
        borderTop: "1px solid rgba(255, 255, 255, 0.08)",
        fontSize: 11,
        lineHeight: 1.45,
      }}
    >
      <span
        style={{
          fontSize: 10,
          letterSpacing: 0.5,
          color: "var(--text-dim)",
          textTransform: "uppercase",
        }}
      >
        Identification · {t.vesselType}
        {t.state !== undefined && t.state !== "underway"
          ? ` · ${t.state.replace(/-/g, " ")}`
          : ""}
      </span>
      <Stack direction="row" gap={10} align="start" style={{ marginTop: 6 }}>
        <Stack gap={6} style={{ flexShrink: 0, alignItems: "center" }}>
          {shape !== undefined ? (
            <DayShapeIcon shape={shape} size={36} />
          ) : (
            <NoShapePlaceholder />
          )}
          {lights !== undefined && (
            <LightArrangementDiagram arrangement={lights} width={84} />
          )}
        </Stack>
        <Stack gap={6} style={{ minWidth: 0 }}>
          <Detail
            label="Day shape"
            value={
              shape !== undefined
                ? describeDayShape(shape)
                : "Vessel underway — no day shape required (Rule 23)"
            }
          />
          {lights !== undefined && (
            <Detail label="Night lights" value={lights.description} />
          )}
          {fog !== undefined && (
            <Detail
              label="Fog signal"
              value={fog.description}
              prefix={
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    color: "var(--accent)",
                    letterSpacing: 1.5,
                  }}
                >
                  {fog.pattern}
                </span>
              }
            />
          )}
        </Stack>
      </Stack>
    </div>
  );
}

function Detail(props: {
  label: string;
  value: string;
  prefix?: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <Stack direction="row" gap={6} align="center">
        <span
          style={{
            fontSize: 9,
            letterSpacing: 0.5,
            color: "var(--text-dim)",
            textTransform: "uppercase",
          }}
        >
          {props.label}
        </span>
        {props.prefix}
      </Stack>
      <span style={{ color: "var(--text-muted)", display: "block", marginTop: 2 }}>
        {props.value}
      </span>
    </div>
  );
}

/**
 * Placeholder where a day-shape diagram would be — for vessels
 * underway that don't carry one. Subtle outline rather than empty
 * space so the layout doesn't collapse unevenly between targets.
 */
function NoShapePlaceholder(): JSX.Element {
  return (
    <div
      style={{
        width: 36,
        height: 54,
        border: "1px dashed rgba(154, 166, 184, 0.25)",
        borderRadius: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 9,
        color: "var(--text-dim)",
        textAlign: "center",
        lineHeight: 1.2,
      }}
    >
      no shape
    </div>
  );
}

function Stat(props: {
  label: string;
  value: string;
  tone?: "danger" | "accent" | "neutral";
}): JSX.Element {
  const color =
    props.tone === "danger"
      ? "var(--danger)"
      : props.tone === "accent"
        ? "var(--accent)"
        : "var(--text)";
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 60 }}>
      <span
        style={{
          fontSize: 10,
          letterSpacing: 0.5,
          color: "var(--text-dim)",
        }}
      >
        {props.label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          color,
          fontWeight: 600,
        }}
      >
        {props.value}
      </span>
    </div>
  );
}
